import { z } from 'zod';
import { IdSchema, InstantSchema, LocalDateSchema } from '../common/index.js';

/**
 * Actes notariés du dossier (réunion du 16/10 #33, REF-REC), ajout PARITE-4 au contrat 1.0.0 : notoriétés déjà obtenues,
 * attestations immobilières, partages… par dossier et par succession, avec leur statut. Le fichier de l'acte reste une pièce de
 * la GED (`documentId`) : jamais une seconde copie. Le type est un code du catalogue versionné servi par l'API (`types`).
 * Audience « staff » seulement tant que l'espace clerc et notaire (X11, X12) ne les reprend pas.
 */
export const ActeNotarieStatusSchema = z.enum(['a_preparer', 'en_cours', 'signe', 'publie']).meta({ id: 'ActeNotarieStatus' });

const CodeTypeActe = z.string().regex(/^[a-z][a-z_]{1,48}$/, 'code du catalogue attendu');
const Facultatif = (max: number) => z.string().trim().min(1).max(max).nullable().optional();

export const ActeNotarieSchema = z
  .object({
    id: IdSchema,
    dossierId: IdSchema,
    successionId: IdSchema.nullable(),
    deceasedDisplayName: z.string().nullable(),
    kind: CodeTypeActe,
    kindLabel: z.string().min(1),
    status: ActeNotarieStatusSchema,
    actDate: LocalDateSchema.nullable(),
    etude: z.string().nullable(),
    reference: z.string().nullable(),
    documentId: IdSchema.nullable(),
    documentTitle: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: InstantSchema,
    createdByDisplayName: z.string(),
    rowVersion: z.number().int().min(1),
  })
  .meta({ id: 'ActeNotarie', description: 'Acte notarié du dossier (déjà obtenu ou à établir), fichier dans la GED.' });

export const ActeNotarieCreateSchema = z
  .object({
    kind: CodeTypeActe,
    status: ActeNotarieStatusSchema,
    successionId: IdSchema.nullable().optional(),
    actDate: LocalDateSchema.nullable().optional(),
    etude: Facultatif(200),
    reference: Facultatif(120),
    documentId: IdSchema.nullable().optional(),
    note: Facultatif(1000),
  })
  .meta({ id: 'ActeNotarieCreate' });

export const ActeNotariePatchSchema = ActeNotarieCreateSchema.partial()
  .refine((p) => Object.keys(p).length > 0, { message: 'au moins un champ à modifier' })
  .meta({ id: 'ActeNotariePatch' });

export const ActesNotariesDossierSchema = z
  .object({
    types: z.array(z.object({ code: CodeTypeActe, label: z.string().min(1) })),
    successions: z.array(z.object({ id: IdSchema, deceasedDisplayName: z.string().min(1) })),
    actes: z.array(ActeNotarieSchema),
  })
  .meta({ id: 'ActesNotariesDossier', description: 'Types du catalogue versionné, successions du dossier et actes.' });
